import { useMemo } from 'react';
import { ActorLogicFrom, ActorRefFrom, fromPromise } from 'xstate';
import alloy from '@alloyidentity/web-sdk';
import { isLeft } from 'fp-ts/lib/Either';
import {
  FraudAlloyInitResponse,
  IDUploadJourneyResponse,
} from '@ads-bread/shared/bread/codecs';
import { getFraudAlloy } from '../../../../lib/handlers/get-fraud-alloy';
import { createLogger } from '../../../../lib/logger';
import { UnauthorizedError, useFetch } from '../../../../lib/hooks/apiFetch';
import { useMerchantID } from '../../../../components/XPropsContext';
import { useAppConfig } from '../../../../components/AppConfigContext';
import { useAuthentication } from '../../../../components/AuthenticationMachineContext';
import { useMerchantConfig } from '../../../../components/MerchantContext';
import { shouldUseMockProvider } from '../../../../lib/mirage/utils';
import { fraudAlloyMachine } from './fraudAlloyMachine';
import {
  InitializingFraudAlloyResult,
  StartIDUploadJourneyResult,
} from './types/actors';
import { mockAlloy } from './utils';
import { assignFraudAlloyInitResponse } from './assigns';
import { emitFraudAlloyMachineReady } from './actions';

const logger = createLogger({
  name: 'checkout.fraud.alloy',
});

export type FraudAlloyMachineActorRef = ActorRefFrom<typeof fraudAlloyMachine>;

export type FraudAlloyMachineActorRefLogic = ActorLogicFrom<
  typeof fraudAlloyMachine
>;

export const useFraudAlloyMachineLogic = (): FraudAlloyMachineActorRefLogic => {
  const apiFetch = useFetch();
  const { logoutUnauthorized } = useAuthentication();
  const {
    environment,
    fraudAlloyBaseAPIKey,
    fraudAlloyBaseJourneyToken,
    tenant,
  } = useAppConfig();
  const { checkoutPrimaryColor, checkoutSecondaryColor } = useMerchantConfig();
  const { merchantID } = useMerchantID();

  const fraudAlloyMachineLogic = useMemo(() => {
    return fraudAlloyMachine.provide({
      guards: {
        isIDUploadJourneySuccess: (_, params) => {
          return (
            params.idUploadJourneyResponse.journey_application_status ===
            'Approved'
          );
        },
      },
      actions: {
        assignFraudAlloyInitResponse,
        emitFraudAlloyMachineReady,
        logIDUploadJourneyFailure: (_, params) => {
          logger.error(
            { ...params.idUploadJourneyResponse, merchantID },
            'FraudAlloyMachine: ID Upload Journey failed'
          );
        },
        logError: (_, params) => {
          logger.error(
            { err: params.error, merchantID },
            `Error in FraudAlloyMachine: ${params.type}`
          );
        },
      },
      actors: {
        initializeFraudAlloy: fromPromise<InitializingFraudAlloyResult>(
          async () => {
            try {
              const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

              if (tenant !== 'ADS') {
                return { fraudAlloyRes: undefined };
              }

              const alloyInit = await alloyLib.init({
                key: fraudAlloyBaseAPIKey,
                production: environment === 'production',
                journeyToken: fraudAlloyBaseJourneyToken,
              });

              const decoded = FraudAlloyInitResponse.decode(alloyInit);

              if (isLeft(decoded)) {
                logger.warn(
                  'Unexpected response from Alloy init. Continuing without FraudAlloy',
                  {
                    cause: alloyInit,
                  }
                );
                return { fraudAlloyRes: undefined };
              }

              const fraudAlloyRes: FraudAlloyInitResponse = decoded.right;

              return { fraudAlloyRes };
            } catch (error) {
              throw { error };
            }
          }
        ),
        startIDUploadJourney: fromPromise(async ({ input }) => {
          try {
            const res = await getFraudAlloy(input.applicationId, apiFetch);

            if (res.error) {
              if (res.error instanceof UnauthorizedError) {
                logoutUnauthorized();
              }
              throw res.error;
            }

            const {
              journey_token: journeyToken,
              journey_application_token: journeyApplicationToken,
              journey_sdk_key: sdkKey,
            } = res.result;

            const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

            await alloyLib.init({
              key: sdkKey,
              production: environment === 'production',
              journeyApplicationToken: journeyApplicationToken,
              journeyToken: journeyToken,
              /**
               * This flag is for multi applicant flows. If set to true we would need to
               * init alloy with the Entity ID of the applicant so that only their portion
               * would be displayed. As of 07/2024 we do not support multi applicant flows.
               */
              isSingleEntity: false,
              showHeader: true,
              apiUrl: 'https://docv.alloy.co/',
              appUrl: 'https://alloysdk.alloy.co',
              color: {
                primary: checkoutPrimaryColor || '',
                secondary: checkoutSecondaryColor || '',
              },
            });

            return await new Promise<StartIDUploadJourneyResult>(
              (resolve, reject) => {
                const callback = (data: unknown) => {
                  logger.info(
                    { data, merchantID },
                    'FraudAlloyMachine: Received callback from Alloy ID Upload Journey'
                  );

                  alloyLib.close();

                  const decoded = IDUploadJourneyResponse.decode(data);

                  if (isLeft(decoded)) {
                    reject(
                      new Error(
                        'Unable to decode fraud alloy ID Upload data!',
                        {
                          cause: data,
                        }
                      )
                    );
                  } else {
                    resolve({ idUploadJourneyResponse: decoded.right });
                  }
                };

                alloyLib.open(callback, input.domID);
              }
            );
          } catch (error) {
            throw { error };
          }
        }),
      },
    });
  }, []);

  return fraudAlloyMachineLogic;
};
